import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import SignaturePad from '../components/SignaturePad';
import api from '../api/axios';

const CACHE_BUSTER = Date.now();

const getCleanImageUrl = (url) => {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`;
};

const Register = () => {
    const { register, login } = useAuth();
    const { config, loading: configLoading } = useConfig();
    const [userData, setUserData] = useState({
        name: '', last_name: '', email: '', identification: '', city: '', country: 'Colombia', representative: '', signature: null
    });
    const [signatureAccepted, setSignatureAccepted] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [eventStarted, setEventStarted] = useState(false);
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const checkEventStatus = async () => {
            try {
                const res = await api.get('/agenda?status=active');
                if (res.data && res.data.length > 0) {
                    const sorted = [...res.data].sort((a, b) => {
                        const dateA = new Date(`${a.date.split('T')[0]}T${a.start_time}`);
                        const dateB = new Date(`${b.date.split('T')[0]}T${b.start_time}`);
                        return dateA - dateB;
                    });
                    const earliest = new Date(`${sorted[0].date.split('T')[0]}T${sorted[0].start_time}`);
                    const now = new Date();
                    if (now >= earliest) {
                        setEventStarted(true);
                    }
                }
            } catch (err) {
                console.error("Error checking event status", err);
            }
        };
        checkEventStatus();
    }, []);

    const isCoherentWord = (str) => {
        if (!str || str.trim().length === 0) return false;
        const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s'-]+$/;
        if (!regex.test(str)) return false;
        if (/(.)\1\1/i.test(str)) return false;
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        if (reg.identification !== false) {
            if (!/^[0-9]+$/.test(userData.identification)) {
                setError('La identificación debe contener solo números.');
                setIsLoading(false);
                return;
            }
        }

        if (reg.terms_accepted && !termsAccepted) {
            setError('Debe aceptar el tratamiento de datos para continuar.');
            setIsLoading(false);
            return;
        }

        if (reg.name !== false && !isCoherentWord(userData.name)) {
            setError('El nombre ingresado no es válido.');
            setIsLoading(false);
            return;
        }

        if (reg.last_name !== false && !isCoherentWord(userData.last_name)) {
            setError('El apellido ingresado no es válido.');
            setIsLoading(false);
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(userData.email)) {
            setError('Debe ingresar un correo electrónico válido.');
            setIsLoading(false);
            return;
        }

        const finalData = { ...userData };
        if (config?.registration_fields?.identification === false) {
            finalData.identification = userData.email;
        }

        const result = await register(finalData);
        if (result.success) {
            await login(finalData.identification);
            navigate('/');
        } else {
            setError(result.message);
            setIsLoading(false);
        }
    };

    if (configLoading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0a58ca] rounded-full animate-spin"></div>
            </div>
        );
    }

    const reg = config?.registration_fields || {};
    const primaryColor = '#c20042';
    const secondaryColor = '#810691';

    const getColSpan = (field, peer) => {
        if (peer && reg[peer] === false) return 'col-span-2';
        return 'col-span-2 sm:col-span-1';
    };

    const logoUrl = '/assets/cardio/CS_Logo.png';
    const bgImage = isMobile ? '/assets/cardio/CS_Fondo-vertical.png' : '/assets/cardio/CS_Fondo-Horizontal.png';

    const containerStyle = {
        backgroundColor: '#550043',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        minHeight: '100vh',
    };

    return (
        <div
            style={containerStyle}
            className="min-h-screen flex flex-col justify-between items-center font-sans relative overflow-x-hidden"
        >
            {/* Logo oficial Cardio Summit 2026 */}
            <div className="relative z-50 w-full flex justify-center pointer-events-none flex-shrink-0 pt-8 pb-4">
                <img
                    src={logoUrl}
                    alt="Cardio Summit 2026"
                    className="w-[280px] sm:w-[340px] h-auto object-contain drop-shadow-xl"
                />
            </div>

            {/* Form Container (Página 3 PDF) */}
            <div
                style={{
                    borderColor: '#c20042',
                    backgroundColor: 'rgba(85, 0, 67, 0.75)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)'
                }}
                className="w-full max-w-lg z-10 space-y-0 border-2 rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] relative mt-10 mb-8 mx-4 animate-fadeIn"
            >
                {/* Floating Title Badge en morado #810691 */}
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 z-20 w-[85%] sm:w-[75%] flex justify-center">
                    <div
                        style={{ backgroundColor: '#810691' }}
                        className="text-white py-3 px-6 text-center font-black text-xl sm:text-2xl rounded-2xl shadow-[0_8px_20px_rgba(129,6,145,0.4)] uppercase tracking-wider w-full"
                    >
                        REGISTRO
                    </div>
                </div>

                <div className="pt-12 pb-8 px-6 sm:px-10 relative">
                    <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-y-5 gap-x-4">
                        {/* Identification / Cédula */}
                        {reg.identification !== false && (
                            <div className="col-span-2 space-y-1 text-left">
                                <label className="text-xs font-bold text-white block mb-1">
                                    Cédula*
                                </label>
                                <input
                                    className="w-full bg-white border border-transparent p-3 rounded-xl focus:outline-none transition-all font-bold text-slate-800 shadow-sm"
                                    value={userData.identification}
                                    onChange={(e) => setUserData({ ...userData, identification: e.target.value.replace(/\D/g, '') })}
                                    required
                                />
                            </div>
                        )}

                        {/* Nombres */}
                        {reg.name !== false && (
                            <div className={`${getColSpan('name', 'last_name')} space-y-1 text-left`}>
                                <label className="text-xs font-bold text-white block mb-1">
                                    Nombres*
                                </label>
                                <input
                                    className="w-full bg-white border border-transparent p-3 rounded-xl focus:outline-none transition-all font-bold text-slate-800 shadow-sm"
                                    value={userData.name}
                                    onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                                    required
                                />
                            </div>
                        )}

                        {/* Apellidos */}
                        {reg.last_name !== false && (
                            <div className={`${getColSpan('last_name', 'name')} space-y-1 text-left`}>
                                <label className="text-xs font-bold text-white block mb-1">
                                    Apellidos*
                                </label>
                                <input
                                    className="w-full bg-white border border-transparent p-3 rounded-xl focus:outline-none transition-all font-bold text-slate-800 shadow-sm"
                                    value={userData.last_name}
                                    onChange={(e) => setUserData({ ...userData, last_name: e.target.value })}
                                    required
                                />
                            </div>
                        )}

                        {/* Correo Electrónico */}
                        <div className="col-span-2 space-y-1 text-left">
                            <label className="text-xs font-bold text-white block mb-1">
                                Correo Electrónico*
                            </label>
                            <input
                                type="email"
                                className="w-full bg-white border border-transparent p-3 rounded-xl focus:outline-none transition-all font-bold text-slate-800 shadow-sm"
                                value={userData.email}
                                onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                                required
                            />
                        </div>

                        {/* Ciudad */}
                        {reg.city !== false && (
                            <div className={`${getColSpan('city', 'country')} space-y-1 text-left`}>
                                <label className="text-xs font-bold text-white block mb-1">
                                    Ciudad*
                                </label>
                                <input
                                    className="w-full bg-white border border-transparent p-3 rounded-xl focus:outline-none transition-all font-bold text-slate-800 shadow-sm"
                                    value={userData.city}
                                    onChange={(e) => setUserData({ ...userData, city: e.target.value })}
                                    required
                                />
                            </div>
                        )}

                        {/* País */}
                        {reg.country !== false && (
                            <div className={`${getColSpan('country', 'city')} space-y-1 text-left`}>
                                <label className="text-xs font-bold text-white block mb-1">
                                    País*
                                </label>
                                <input
                                    className="w-full bg-white border border-transparent p-3 rounded-xl focus:outline-none transition-all font-bold text-slate-800 shadow-sm"
                                    value={userData.country}
                                    onChange={(e) => setUserData({ ...userData, country: e.target.value })}
                                    required
                                />
                            </div>
                        )}

                        {/* Campos obligatorios & Términos */}
                        <div className="col-span-2 pt-1 flex flex-col gap-2">
                            <span className="text-[11px] font-medium text-white/90 block text-left">*Campos obligatorios</span>

                            <label className="flex items-center gap-3 cursor-pointer group mt-1">
                                <div className="relative flex items-center justify-center">
                                    <input
                                        type="checkbox"
                                        className="peer sr-only"
                                        checked={termsAccepted}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                        required
                                    />
                                    <div
                                        style={{ backgroundColor: termsAccepted ? '#c20042' : '#ffffff' }}
                                        className="w-5 h-5 rounded-md border border-white/40 transition-all flex items-center justify-center shadow-sm"
                                    >
                                        <svg className={`w-3.5 h-3.5 text-white transform transition-transform ${termsAccepted ? 'scale-100' : 'scale-0'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                                <span className="text-[11px] text-white leading-snug select-none text-left">
                                    Acepto el tratamiento de datos personales. <span className="underline cursor-pointer">Más información aquí</span>
                                </span>
                            </label>
                        </div>

                        {/* Submit Button ENVIAR en #c20042 */}
                        <div className="col-span-2 pt-4 flex flex-col items-center">
                            {error && (
                                <div className="bg-red-950/80 text-red-200 p-3 rounded-xl text-[11px] font-bold tracking-wide border border-red-500/30 text-center mb-4 w-full">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading || !termsAccepted}
                                style={{ backgroundColor: '#c20042' }}
                                className="w-[75%] text-white py-3.5 rounded-2xl font-black text-lg uppercase tracking-wider shadow-[0_8px_20px_rgba(194,0,66,0.5)] hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                            >
                                {isLoading ? 'ENVIANDO...' : 'ENVIAR'}
                            </button>
                        </div>

                        <div className="col-span-2 text-center pt-2">
                            <Link to="/login" className="text-xs font-semibold text-white/80 hover:text-white transition-colors">
                                ← Volver al ingreso
                            </Link>
                        </div>
                    </form>
                </div>
            </div>

            {/* Footer legal con avales y logo Adium idéntico al PDF */}
            <footer className="w-full px-4 sm:px-6 py-6 flex-shrink-0 flex flex-col items-center gap-6 z-40 select-none bg-transparent max-w-4xl mx-auto">
                {/* Tarjeta de Avales de Asociaciones Médicas idéntica al diseño */}
                <div className="w-full flex justify-center">
                    <img
                        src="/assets/cardio/Logos_Asociaciones.png"
                        alt="CardioSummit 2026, un evento con el aval de: SCC, ASOCOLNEF, ACE"
                        className="w-full max-w-3xl h-auto object-contain drop-shadow-xl rounded-[28px] pointer-events-auto"
                    />
                </div>

                <div className="w-full flex items-center justify-between gap-4">
                    <div className="flex flex-col items-center flex-shrink-0">
                        <img
                            src="/assets/cardio/CS_LogoAdium.png"
                            alt="Adium"
                            className="h-10 sm:h-12 w-auto object-contain drop-shadow"
                        />
                        <span className="text-[11px] font-bold text-white tracking-wider mt-1">Adium</span>
                    </div>
                    <div className="flex-1 text-[8px] sm:text-[9px] text-white/80 leading-snug text-left pl-2">
                        Este evento está dirigido exclusivamente al cuerpo médico y es de carácter personal e intransferible. Adium no promociona ni promueve el uso de sus productos / medicamentos en forma diferente al aprobado por la autoridad regulatoria e incluida en la información de prescripción o ficha técnica. Para mayor información comunicarse con el departamento médico de Adium S.A.S. Carrera 16 No. 85-96. Bogotá D.C. - Teléfono: +601 6460505. Si conoce un evento adverso de nuestros medicamentos, por favor reportarlo a farmacovigilancia@adium.com.co. CO--2600409
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Register;
